import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import AdminActivityDashboard from './AdminActivityDashboard';
import TempSuperAdminDashboard from './TempSuperAdminDashboard';
import OmsOperationsDashboardPage from '../../features/oms/dashboard/OmsOperationsDashboardPage';
import AdminAnalyticsPage from '../../features/oms/adminAnalytics/AdminAnalyticsPage';

/**
 * Resolves display role when localStorage `userRole` is missing but `user` / department roles exist.
 */
function useEffectiveRoleName(): string {
  const { role, user, activeDepartment, rolesByDepartment } = useAuth();
  return useMemo(() => {
    const trimmed = (role || '').trim();
    if (trimmed) return trimmed;

    const fromUser =
      (user && typeof user.role === 'object' && user.role?.name) ||
      user?.roleName ||
      '';
    if (fromUser) return String(fromUser);

    const deptEntry = rolesByDepartment?.[activeDepartment];
    if (deptEntry && typeof deptEntry === 'object' && 'name' in deptEntry) {
      const n = (deptEntry as { name?: string }).name;
      if (n) return String(n);
    }

    return '';
  }, [role, user, activeDepartment, rolesByDepartment]);
}

export default function Home() {
  const { activeDepartment } = useAuth();
  const effectiveRole = useEffectiveRoleName();
  const roleLower = effectiveRole.toLowerCase();
  const dept = (activeDepartment || 'OMS').trim().toUpperCase();

  if (roleLower.includes('super')) {
    return <TempSuperAdminDashboard />;
  }

  // OMS Manager / Head → unified OMS Command Center.
  if (dept === 'OMS' && (roleLower.includes('head') || roleLower.includes('manager'))) {
    return <OmsOperationsDashboardPage />;
  }

  // OMS Admin → platform-health Admin Analytics (also at /admin/analytics).
  if (dept === 'OMS' && roleLower.includes('admin')) {
    return <AdminAnalyticsPage />;
  }

  // Non-OMS departments keep the pre-existing /dashboard surface (EPC has
  // its own role dashboards under /epc/dashboards/*, reached via sidebar).
  if (dept !== 'OMS') {
    return <TempSuperAdminDashboard />;
  }

  return <AdminActivityDashboard />;
}
